import { useState } from 'react';
import { Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { FormikHelpers } from 'formik';
import type { CloudUploadScope } from 'api/apiCloudflare';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgFile from 'components/svg/SvgFile';
import SvgSave from 'components/svg/SvgSave';
import type { ServiceEditType } from '../types';
import ServiceFormAdditional from './tabs/ServiceFormAdditional';
import ServiceFormFiles from './tabs/ServiceFormFiles';
import ServiceFormGeneral from './tabs/ServiceFormGeneral';

interface ServiceFormProps {
    service: ServiceEditType;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    setFieldValue: FormikHelpers<ServiceEditType>['setFieldValue'];
    values: ServiceEditType;
    ensureSaved: () => Promise<CloudUploadScope | false>;
}

const ServiceForm = ({
    service,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    ensureSaved,
}: ServiceFormProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Services', 'common']);
    const [activeTab, setActiveTab] = useState('base');

    return (
        <Form onSubmit={handleSubmit} noValidate className="d-flex flex-column h-100">
            <div className="off-product-card-tabs d-flex flex-column h-100">
                <div className="off-product-card-tabs-content px-3">
                    <Tabs
                        variant="underline"
                        activeKey={activeTab}
                        onSelect={(tab) => tab && setActiveTab(tab)}
                        className="mb-3 invoice-tab border-bottom"
                    >
                        <Tab
                            eventKey="base"
                            title={
                                <>
                                    <i className="bi bi-gear me-2 fs-14"></i>
                                    {t('tabGeneral')}
                                </>
                            }
                        >
                            <ServiceFormGeneral
                                service={service}
                                isSubmitting={isSubmitting}
                                setFieldValue={setFieldValue}
                                values={values}
                            />
                        </Tab>

                        <Tab
                            eventKey="files"
                            title={
                                <>
                                    <SvgFile height={14} className="me-1" />
                                    {t('tabFiles')}
                                </>
                            }
                        >
                            <div className="pb-4">
                                <ServiceFormFiles
                                    setFieldValue={setFieldValue}
                                    values={values}
                                    ensureSaved={ensureSaved}
                                />
                            </div>
                        </Tab>

                        <Tab
                            eventKey="additional"
                            title={
                                <>
                                    <i className="bi bi-card-list me-2 fs-14"></i>
                                    {t('tabAdditional')}
                                </>
                            }
                        >
                            <ServiceFormAdditional values={values} />
                        </Tab>
                    </Tabs>
                </div>

                {activeTab === 'base' && (
                    <div className="p-3 offcanvas-footer off-product-card-tabs-footer">
                        {hasUserAccess(auth?.user, 'prodEdit') ? (
                            <div className="d-flex justify-content-end">
                                <Button type="submit" variant="info" disabled={isSubmitting} className="min100px">
                                    {spinnerTitle(
                                        <>
                                            <SvgSave className="me-2 mtop-2" />
                                            {t('common:buttons.save')}
                                        </>,
                                        isSubmitting,
                                        t('common:buttons.saving'),
                                    )}
                                </Button>
                            </div>
                        ) : (
                            <div className="alert alert-warning text-start" role="alert">
                                {t('common:messReadOnly')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Form>
    );
};

export default ServiceForm;
